/**
 * Optional LLM parser — the pluggable alternative to the deterministic one.
 *
 * Crucially it does NOT emit SPARQL. It emits the same structured `PatternMatch`
 * the regex templates would, which then flows through the identical grounding +
 * IR-building path (`groundAndBuild`). So an LLM only ever helps with the fuzzy
 * "what is this question asking" step; entity/property grounding, validation and
 * execution stay deterministic. The model is injected as a `complete()` function,
 * so this works with a browser BYOK key or a server-held key without change.
 */
import type { EntityLinker, RelationLinker } from "../linking/types.ts";
import { groundAndBuild } from "../analysis/ground.ts";
import type { PatternKind, PatternMatch } from "../analysis/patterns.ts";
import type { QueryForm } from "../ir/types.ts";
import type { ParseContext, ParseResult, SemanticParser } from "./types.ts";
import { ParseError } from "./types.ts";

/** Injected model call: prompt in, completion text out. */
export type CompleteFn = (prompt: string, signal?: AbortSignal) => Promise<string>;

export interface LlmParserOptions {
  complete: CompleteFn;
  entityLinker: EntityLinker;
  relationLinker: RelationLinker;
  sourceId?: string;
}

const VALID_KINDS: PatternKind[] = [
  "attribute-of",
  "possessive",
  "active-who",
  "relation-list",
  "count-of",
  "count-class",
  "superlative",
];
const VALID_FORMS: QueryForm[] = ["select", "ask", "count"];

const SCHEMA_PROMPT = `You translate a natural-language question into a small JSON "match" object.
Do NOT write SPARQL. Only output JSON (no prose, no code fences) with these fields:
{
  "kind": one of ${JSON.stringify(VALID_KINDS)},
  "form": one of ${JSON.stringify(VALID_FORMS)},
  "entityMention": string | null   // the named thing, e.g. "France", "Christopher Nolan"
  "relationPhrase": string | null  // the relation, e.g. "capital", "directed by"
  "classNoun": string | null       // a class, e.g. "films", "mountains"
  "superlativeAdj": string | null  // e.g. "tallest", "most populous"
  "entityRole": "subject" | "object"  // where the entity sits; the answer takes the other slot
}
Examples:
Q: "capital of France" -> {"kind":"attribute-of","form":"select","entityMention":"France","relationPhrase":"capital","classNoun":null,"superlativeAdj":null,"entityRole":"subject"}
Q: "films directed by Christopher Nolan" -> {"kind":"relation-list","form":"select","entityMention":"Christopher Nolan","relationPhrase":"directed by","classNoun":"films","superlativeAdj":null,"entityRole":"object"}
Q: "tallest mountain" -> {"kind":"superlative","form":"select","entityMention":null,"relationPhrase":null,"classNoun":"mountain","superlativeAdj":"tallest","entityRole":"subject"}`;

function extractJson(text: string): unknown {
  const start = text.indexOf("{");
  const end = text.lastIndexOf("}");
  if (start < 0 || end <= start) throw new ParseError("LLM did not return a JSON object.");
  try {
    return JSON.parse(text.slice(start, end + 1));
  } catch {
    throw new ParseError("LLM returned malformed JSON.");
  }
}

function toPatternMatch(raw: unknown): PatternMatch {
  if (typeof raw !== "object" || raw === null) throw new ParseError("LLM match is not an object.");
  const o = raw as Record<string, unknown>;
  const kind = o["kind"];
  const form = o["form"];
  const entityRole = o["entityRole"];
  if (typeof kind !== "string" || !VALID_KINDS.includes(kind as PatternKind)) {
    throw new ParseError(`LLM returned unknown kind "${String(kind)}".`);
  }
  if (typeof form !== "string" || !VALID_FORMS.includes(form as QueryForm)) {
    throw new ParseError(`LLM returned unknown form "${String(form)}".`);
  }
  const role = entityRole === "object" ? "object" : "subject";
  const str = (key: string): string | undefined => {
    const val = o[key];
    return typeof val === "string" && val.trim() !== "" ? val.trim() : undefined;
  };
  const match: PatternMatch = {
    kind: kind as PatternKind,
    form: form as QueryForm,
    template: `llm:${kind}`,
    entityRole: role,
  };
  const entityMention = str("entityMention");
  const relationPhrase = str("relationPhrase");
  const classNoun = str("classNoun");
  const superlativeAdj = str("superlativeAdj");
  if (entityMention) match.entityMention = entityMention;
  if (relationPhrase) match.relationPhrase = relationPhrase;
  if (classNoun) match.classNoun = classNoun;
  if (superlativeAdj) match.superlativeAdj = superlativeAdj;
  return match;
}

export class LlmParser implements SemanticParser {
  readonly name = "llm";
  private readonly complete: CompleteFn;
  private readonly entityLinker: EntityLinker;
  private readonly relationLinker: RelationLinker;
  private readonly sourceId: string;

  constructor(opts: LlmParserOptions) {
    this.complete = opts.complete;
    this.entityLinker = opts.entityLinker;
    this.relationLinker = opts.relationLinker;
    this.sourceId = opts.sourceId ?? "wikidata";
  }

  async parse(question: string, ctx?: ParseContext): Promise<ParseResult> {
    const prompt = `${SCHEMA_PROMPT}\n\nQ: ${JSON.stringify(question)} ->`;
    const completion = await this.complete(prompt, ctx?.signal);
    const match = toPatternMatch(extractJson(completion));
    return groundAndBuild(match, {
      entityLinker: this.entityLinker,
      relationLinker: this.relationLinker,
      sourceId: this.sourceId,
      parserName: this.name,
      question,
      ...(ctx?.signal ? { signal: ctx.signal } : {}),
      ...(ctx?.overrides ? { overrides: ctx.overrides } : {}),
    });
  }
}
